import {
	FilterContextValue,
	FilterLoadingState,
	FilterOption,
	FilterRegistry,
	FilterSearchData,
	FilterService,
	FilterState,
} from '@supply-mover-2-0/core';
import React, {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useState,
} from 'react';

const FilterContext = createContext<FilterContextValue | null>(null);

interface FilterProviderProps {
	children: ReactNode;
	initialFilters?: FilterState;
	initialLockedFilters?: FilterState;
	initialBoundedFilters?: FilterState;
}

/**
 * FilterProvider - Global filter state management
 *
 * Provides centralized state management for all filters across the application.
 * Handles both active filters and pending filters (for mobile filter trays).
 */
export const FilterProvider: React.FC<FilterProviderProps> = ({
	children,
	initialFilters = {},
	initialLockedFilters = {},
	initialBoundedFilters = {},
}) => {
	// Active filter state — merge in locked filters so they're always present
	const [filters, setFilters] = useState<FilterState>({
		...initialFilters,
		...initialLockedFilters,
	});

	// Locked filter state (filters that cannot be removed)
	const [lockedFilters, setLockedFilters] =
		useState<FilterState>(initialLockedFilters);

	// Bounded filter state (filters restricted to a set of allowed values)
	const [boundedFilters] = useState<FilterState>(initialBoundedFilters);

	// Pending filter state (for mobile filter tray)
	const [pendingFilters, setPendingFilters] =
		useState<FilterState>(initialFilters);

	// Search results cache
	const [searchData, setSearchData] = useState<FilterSearchData>({});

	// Loading states
	const [loading, setLoading] = useState<FilterLoadingState>({});

	// Set a filter value
	const setFilter = useCallback(
		(filterType: string, values: FilterOption[]) => {
			setFilters((prev) => {
				// Get locked values for this filter type
				const locked = lockedFilters[filterType] || [];
				const bounded = boundedFilters[filterType];

				let newValues = [...values];

				// If filter is bounded, only allow values within the bounded set
				if (bounded != null && bounded.length > 0) {
					const boundedIds = new Set(bounded.map((v) => v.id));
					newValues = newValues.filter((v) => boundedIds.has(v.id));

					// Must have at least one value — if empty, keep previous selection
					if (newValues.length === 0) {
						const prevValues = prev[filterType] ?? [];
						newValues = prevValues.length > 0 ? [...prevValues] : [...bounded];
					}
				}

				// Add back any locked values that were removed
				locked.forEach((lockedValue) => {
					if (!newValues.some((v) => v.id === lockedValue.id)) {
						newValues.push(lockedValue);
					}
				});

				return {
					...prev,
					[filterType]: newValues,
				};
			});
		},
		[lockedFilters, boundedFilters],
	);

	// Batch update multiple filters at once
	const setMultipleFilters = useCallback(
		(
			updates: Partial<FilterState>,
			metadata?: {
				isPreset?: boolean;
				source?: 'user' | 'system';
			},
		) => {
			setFilters((prev) => {
				const newState: FilterState = { ...prev };

				// Merge updates, ensuring we don't add undefined values
				Object.entries(updates).forEach(([key, value]) => {
					if (value !== undefined) {
						newState[key] = value;
					}
				});

				return newState;
			});

			// If these are preset filters, update locked filters
			if (metadata?.isPreset) {
				setLockedFilters((prev) => {
					const newLocked: FilterState = { ...prev };

					// Clear all locked filters first (for when switching lists)
					Object.keys(newLocked).forEach((key) => {
						newLocked[key] = [];
					});

					// Set new locked filters
					Object.entries(updates).forEach(([key, value]) => {
						if (
							value !== undefined &&
							Array.isArray(value) &&
							value.length > 0
						) {
							newLocked[key] = value;
						}
					});

					return newLocked;
				});
			}
		},
		[],
	);

	// Set a pending filter value (for mobile filter tray)
	const setPendingFilter = useCallback(
		(filterType: string, values: FilterOption[]) => {
			setPendingFilters((prev) => ({
				...prev,
				[filterType]: values,
			}));
		},
		[],
	);

	// Search for filter options
	const searchFilter = useCallback(
		async (filterType: string, searchTerm: string) => {
			const config = FilterRegistry.getConfig(filterType);
			if (!config) return;

			setLoading((prev) => ({ ...prev, [filterType]: true }));

			try {
				const options = await FilterService.getFilterOptions(
					filterType,
					searchTerm,
				);

				setSearchData((prev) => {
					const newData = {
						...prev,
						[filterType]: options,
					};
					return newData;
				});
			} catch (error) {
				// Error: Failed to search ${filterType}:
				setSearchData((prev) => ({
					...prev,
					[filterType]: [],
				}));
			} finally {
				setLoading((prev) => ({ ...prev, [filterType]: false }));
			}
		},
		[],
	);

	// Apply pending filters to active filters
	const applyPendingFilters = useCallback(() => {
		setFilters({ ...pendingFilters });
	}, [pendingFilters]);

	// Reset pending filters to match active filters
	const resetPendingFilters = useCallback(() => {
		setPendingFilters({ ...filters });
	}, [filters]);

	// Check if there are changes between active and pending filters
	const hasChanges = JSON.stringify(filters) !== JSON.stringify(pendingFilters);

	// Get filter configuration
	const getFilterConfig = useCallback((filterType: string) => {
		return FilterRegistry.getConfig(filterType);
	}, []);

	// Check if a filter is active (has selected values)
	const isFilterActive = useCallback(
		(filterType: string) => {
			const filterValues = filters[filterType];
			return filterValues ? filterValues.length > 0 : false;
		},
		[filters],
	);

	// Get total count of active filters
	const getActiveFilterCount = useCallback(() => {
		return Object.values(filters).reduce((count, filterValues) => {
			return count + (filterValues?.length || 0);
		}, 0);
	}, [filters]);

	const contextValue: FilterContextValue = {
		filters,
		pendingFilters,
		lockedFilters,
		boundedFilters,
		searchData,
		loading,
		setFilter,
		setMultipleFilters,
		setPendingFilter,
		searchFilter,
		applyPendingFilters,
		resetPendingFilters,
		hasChanges,
		getFilterConfig,
		isFilterActive,
		getActiveFilterCount,
	};

	return (
		<FilterContext.Provider value={contextValue}>
			{children}
		</FilterContext.Provider>
	);
};

/**
 * useFilters - Hook to access filter context
 */
export const useFilterContext = (): FilterContextValue => {
	const context = useContext(FilterContext);
	if (!context) {
		throw new Error('useFilterContext must be used within a FilterProvider');
	}
	return context;
};

/**
 * useFilter - Hook to manage a specific filter
 */
export const useFilter = (filterType: string) => {
	const context = useFilterContext();
	const config = context.getFilterConfig(filterType);

	if (!config) {
		throw new Error(`Filter configuration not found for type: ${filterType}`);
	}

	return {
		// Configuration
		config,

		// Current state
		selectedValues: context.filters[filterType] || [],
		pendingValues: context.pendingFilters[filterType] || [],
		lockedValues: context.lockedFilters[filterType] || [],
		searchResults: context.searchData[filterType] || [],
		loading: context.loading[filterType] || false,

		// Actions
		setValues: (values: FilterOption[]) =>
			context.setFilter(filterType, values),
		setPendingValues: (values: FilterOption[]) =>
			context.setPendingFilter(filterType, values),
		search: (searchTerm: string) =>
			context.searchFilter(filterType, searchTerm),

		// Utilities
		isActive: context.isFilterActive(filterType),
		isEmpty: (context.filters[filterType]?.length || 0) === 0,
	};
};
